import { useTranslation } from 'react-i18next';
import { FileDown, FileText, ChevronDown } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useReportDownload } from '../hooks';
import type { Matter } from '../types';

interface MatterReportButtonProps {
  matter: Matter;
}

// Case 360° (Stage 14) — on-demand Matter Report export (PDF / DOCX),
// generated fresh on each click; the server stamps user + time and audits it.
export function MatterReportButton({ matter }: MatterReportButtonProps) {
  const { t } = useTranslation();
  const { downloadReportMutation } = useReportDownload();

  const download = (format: 'pdf' | 'docx') => {
    downloadReportMutation.mutate({ id: matter.id, format });
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" disabled={downloadReportMutation.isPending}>
          <FileDown className="mr-2 h-4 w-4" />
          {downloadReportMutation.isPending
            ? t('report.generating')
            : t('report.export')}
          <ChevronDown className="ml-2 h-3.5 w-3.5 opacity-60" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={() => download('pdf')}>
          <FileText className="mr-2 h-4 w-4 text-red-500" />
          {t('report.exportPdf')}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => download('docx')}>
          <FileText className="mr-2 h-4 w-4 text-blue-500" />
          {t('report.exportDocx')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
